import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Crown, Sparkles, CheckCircle, Users, Calendar, Award, Gift, TrendingUp, Shield } from 'lucide-react';
export function DirectAccessSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-639">
      {/* Mystical background effects - matching reference snippet */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-640" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-641" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-642" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-643">
        {/* MAIN SECTION: 12 MONTHS OF DIRECT ACCESS */}
        <div className="relative mb-20" data-id="element-644">
          <div className="absolute inset-0 bg-gradient-radial from-purple-600/20 via-transparent to-transparent blur-3xl" data-id="element-645" />

          <div className="relative bg-gradient-to-br from-purple-950/60 via-stone-950/80 to-black border-4 border-purple-600/60 p-4 sm:p-8 md:p-12 rounded-lg shadow-[0_0_60px_rgba(168,85,247,0.5)]" data-id="element-646">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12" data-id="element-647">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-transparent" data-id="element-648" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-purple-500 to-transparent" data-id="element-649" />
            </div>
            <div className="absolute top-0 right-0 w-12 h-12" data-id="element-650">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-purple-500 to-transparent" data-id="element-651" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-purple-500 to-transparent" data-id="element-652" />
            </div>
            <div className="absolute bottom-0 left-0 w-12 h-12" data-id="element-653">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-transparent" data-id="element-654" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-purple-500 to-transparent" data-id="element-655" />
            </div>
            <div className="absolute bottom-0 right-0 w-12 h-12" data-id="element-656">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-purple-500 to-transparent" data-id="element-657" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-purple-500 to-transparent" data-id="element-658" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-8" data-id="element-659">
              <div className="relative" data-id="element-660">
                <div className="absolute inset-0 bg-purple-500/40 blur-xl animate-pulse-slow" data-id="element-661" />
                <CheckCircle className="relative w-16 h-16 text-purple-400 drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]" data-id="element-662" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8" data-id="element-663">
              <h3 className="text-xl sm:text-3xl md:text-5xl font-serif font-bold text-purple-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
            }} data-id="element-664">
                ✓ 12 MONTHS OF DIRECT ACCESS TO BRENDEN HENRY & DR. ALI MAZLOUM
              </h3>

              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100 mb-4" data-id="element-665">
                3+ Private Sessions Every Month
              </p>

              <p className="text-base sm:text-xl text-stone-400" data-id="element-666">
                (Worth $18,000+ based on our $1,500/hour private consulting
                rate)
              </p>
            </div>

            {/* Opening copy */}
            <div className="space-y-6 text-lg text-stone-300 mb-12" data-id="element-667">
              <p className="text-2xl font-bold text-center text-amber-400" style={{
              textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
            }} data-id="element-668">
                This is the crown jewel.
              </p>

              <p className="text-xl text-center text-stone-100 font-semibold" data-id="element-669">
                The part that changes everything.
              </p>

              <p className="text-xl text-stone-100" data-id="element-670">
                For 12 months, you get direct access to Brenden Henry and Dr.
                Ali Mazloum.
              </p>

              <div className="bg-purple-950/30 border-l-4 border-purple-500 p-4 sm:p-6 space-y-3" data-id="element-671">
                <p className="text-stone-100" data-id="element-672">
                  Not through some diluted group program where you're one of
                  hundreds fighting for attention.
                </p>
                <p className="text-stone-100" data-id="element-673">
                  Not through some forum where your questions get answered by
                  moderators who don't actually know.
                </p>
                <p className="text-xl font-bold text-purple-400" data-id="element-674">
                  Direct access to Brenden and Ali themselves.
                </p>
              </div>

              <p className="text-xl font-bold text-stone-100" data-id="element-675">
                Here's what these monthly sessions include:
              </p>
            </div>

            {/* LIVE STRATEGIC BRIEFINGS WITH DR. ALI */}
            <div className="mb-16" data-id="element-676">
              <div className="bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-677">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-678">
                  <Users className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400 flex-shrink-0" data-id="element-679" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-emerald-400" data-id="element-680">
                    LIVE STRATEGIC BRIEFINGS WITH DR. ALI MAZLOUM
                  </h4>
                </div>

                <p className="text-2xl text-emerald-300 font-bold mb-6" data-id="element-681">
                  The Human MRI Reveals What Your Doctors Miss
                </p>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-682">
                  <p className="text-stone-100" data-id="element-683">
                    Ali breaks down complex health optimization strategies,
                    diagnostic insights, and transformation protocols.
                  </p>

                  <p className="text-stone-100" data-id="element-684">
                    This is the same level of intelligence he can provide to our
                    $120,000/year private clients.
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-emerald-500/30 my-6" data-id="element-685">
                    <p data-id="element-686">
                      The same analytical frameworks he uses in Russia's largest
                      hospitals.
                    </p>
                    <p data-id="element-687">
                      The same diagnostic genius that spots what entire medical
                      teams miss.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mb-4" data-id="element-688">
                    You'll learn:
                  </p>

                  <div className="space-y-3" data-id="element-689">
                    <p className="flex items-start gap-3" data-id="element-690">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-691">→</span>
                      <span data-id="element-692">
                        How to think diagnostically instead of symptomatically
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-693">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-694">→</span>
                      <span data-id="element-695">
                        How to see patterns in your biomarkers that reveal root
                        causes
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-696">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-697">→</span>
                      <span data-id="element-698">
                        How to identify systemic failures instead of just
                        treating symptoms
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-699">
                      <span className="text-emerald-400 flex-shrink-0" data-id="element-700">→</span>
                      <span data-id="element-701">
                        How to design protocols that actually address the source
                        of your issues
                      </span>
                    </p>
                  </div>

                  <div className="bg-emerald-950/30 p-6 rounded-lg mt-6" data-id="element-702">
                    <p className="text-stone-100 mb-2" data-id="element-703">This isn't theory.</p>
                    <p className="text-stone-100 mb-2" data-id="element-704">
                      This is applied clinical intelligence.
                    </p>
                    <p className="text-xl text-emerald-400 font-bold" data-id="element-705">
                      The kind of intelligence that saves lives when
                      conventional medicine fails.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* MONTHLY INTELLIGENCE DROPS WITH BRENDEN */}
            <div className="mb-16" data-id="element-706">
              <div className="bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-707">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-708">
                  <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-400 flex-shrink-0" data-id="element-709" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-cyan-400" data-id="element-710">
                    MONTHLY INTELLIGENCE DROPS WITH BRENDEN HENRY
                  </h4>
                </div>

                <p className="text-2xl text-cyan-300 font-bold mb-6" data-id="element-711">
                  Inside The Mind That Decoded The Peptide Matrix
                </p>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-712">
                  <p className="text-stone-100" data-id="element-713">
                    Brenden shares cutting-edge research, new peptide
                    discoveries, protocol updates, and answers to the questions
                    the community is asking.
                  </p>

                  <p className="text-xl font-bold text-cyan-400" data-id="element-714">
                    You get inside his brain each month.
                  </p>

                  <div className="bg-cyan-950/30 border-l-4 border-cyan-500 p-6 my-6" data-id="element-715">
                    <p className="text-stone-100 mb-2" data-id="element-716">
                      Not through a polished course recorded years ago.
                    </p>
                    <p className="text-xl font-bold text-cyan-400" data-id="element-717">
                      But recorded new every month. Current. Responsive to what
                      you actually need to know right now.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mb-4" data-id="element-718">
                    He'll break down:
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-cyan-500/30" data-id="element-719">
                    <p data-id="element-720">New information as it's released.</p>
                    <p data-id="element-721">
                      Deeper dives into the most important topics from new
                      angles.
                    </p>
                    <p data-id="element-722">Why certain protocols work better than others.</p>
                    <p data-id="element-723">Insights from his latest client transformations.</p>
                    <p data-id="element-724">Misinformation he's seeing spread by influencers.</p>
                  </div>

                  <p className="text-xl text-cyan-400 font-bold mt-6" data-id="element-725">
                    And he'll do it in a way that makes complex pharmacology
                    accessible without dumbing it down.
                  </p>
                </div>
              </div>
            </div>

            {/* LIVE Q&A SESSIONS */}
            <div className="mb-16" data-id="element-726">
              <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-727">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-728">
                  <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400 flex-shrink-0" data-id="element-729" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-400" data-id="element-730">
                    LIVE "INNER CIRCLE" Q&A SESSIONS
                  </h4>
                </div>

                <p className="text-lg sm:text-xl md:text-2xl text-blue-300 font-bold mb-4 sm:mb-6" data-id="element-731">
                  Get Direct Answers About YOUR Specific Situation
                </p>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-732">
                  <p className="text-xl font-bold text-stone-100" data-id="element-733">
                    This is where theory meets YOUR reality.
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-blue-500/30" data-id="element-734">
                    <p data-id="element-735">Submit your questions in advance or ask them live.</p>
                    <p data-id="element-736">
                      Get direct answers from the masters about YOUR specific
                      situation, YOUR protocols, YOUR results.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mt-6 mb-4" data-id="element-737">
                    Real questions from members like you:
                  </p>

                  <div className="space-y-6" data-id="element-738">
                    <div className="bg-blue-950/30 p-6 rounded-lg" data-id="element-739">
                      <p className="text-stone-100 mb-3 italic" data-id="element-740">
                        "I'm stacking these three peptides for cognitive
                        enhancement but I'm not seeing the results I expected.
                        What am I missing?"
                      </p>
                      <p className="text-blue-400 font-semibold" data-id="element-741">
                        → Brenden can tell you exactly what's wrong and how to
                        fix it.
                      </p>
                    </div>

                    <div className="bg-blue-950/30 p-6 rounded-lg" data-id="element-742">
                      <p className="text-stone-100 mb-3 italic" data-id="element-743">
                        "My bloodwork shows this pattern but my doctor doesn't
                        know what it means. What's actually happening?"
                      </p>
                      <p className="text-blue-400 font-semibold" data-id="element-744">
                        → Ali can decode it in minutes.
                      </p>
                    </div>

                    <div className="bg-blue-950/30 p-6 rounded-lg" data-id="element-745">
                      <p className="text-stone-100 mb-3 italic" data-id="element-746">
                        "I want to address multiple issues — chronic pain,
                        cognitive decline, and hormone optimization. How do I
                        prioritize?"
                      </p>
                      <p className="text-blue-400 font-semibold" data-id="element-747">
                        → They will show you exactly how to think about it and
                        sequence your approach in the most effective way.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500 p-4 sm:p-6 md:p-8 mt-6 sm:mt-8" data-id="element-748">
                    <p className="text-xl text-stone-100 mb-4" data-id="element-749">
                      This alone would cost you{' '}
                      <span className="text-amber-400 font-bold" data-id="element-750">
                        $1,500 per hour
                      </span>{' '}
                      if you booked Brenden or Ali privately.
                    </p>
                    <p className="text-xl text-stone-100 mb-4" data-id="element-751">
                      You're getting{' '}
                      <span className="text-blue-400 font-bold" data-id="element-752">36+ hours</span>{' '}
                      over 12 months.
                    </p>
                    <p className="text-2xl text-amber-400 font-bold" data-id="element-753">
                      That's over $54,000 in value based on private consulting
                      rates.
                    </p>
                  </div>

                  <p className="text-xl font-bold text-stone-100 mt-6" data-id="element-754">
                    But the real value is incalculable.
                  </p>

                  <p className="text-stone-100" data-id="element-755">
                    Because getting the right answer at the right time can:
                  </p>

                  <div className="space-y-3 pl-6 border-l-2 border-blue-500/30 mt-4" data-id="element-756">
                    <p className="flex items-start gap-3" data-id="element-757">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-758">→</span>
                      <span data-id="element-759">Save you months of trial and error</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-760">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-761">→</span>
                      <span data-id="element-762">Prevent costly mistakes</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-763">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-764">→</span>
                      <span data-id="element-765">Unlock results you didn't know were possible</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-766">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-767">→</span>
                      <span data-id="element-768">Reverse conditions you thought were permanent</span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-769">
                      <span className="text-blue-400 flex-shrink-0" data-id="element-770">→</span>
                      <span data-id="element-771">
                        Optimize protocols you thought were already optimized
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* MONTHLY THEMED INTELLIGENCE BRIEFINGS */}
            <div className="mb-16" data-id="element-772">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-773">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-774">
                  <Award className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400 flex-shrink-0" data-id="element-775" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-400" data-id="element-776">
                    ✓ MONTHLY THEMED INTELLIGENCE BRIEFINGS
                  </h4>
                </div>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-777">
                  <p className="text-xl font-bold text-stone-100" data-id="element-778">
                    Now, each month we will have a theme.
                  </p>

                  <p className="text-stone-100" data-id="element-779">
                    Because you're building comprehensive understanding
                    systematically.
                  </p>

                  <p className="text-stone-100" data-id="element-780">
                    Right now we have loosely structured the theme for each
                    month to align with your Peptide Mastery 2.0 content
                    unlocks:
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 my-8" data-id="element-781">
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-782">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-783">Month 1:</p>
                      <p className="text-sm" data-id="element-784">
                        Foundations & Mass Spec Intelligence
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-785">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-786">Month 2:</p>
                      <p className="text-sm" data-id="element-787">
                        Sleep, Circadian & Stress Optimization
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-788">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-789">Month 3:</p>
                      <p className="text-sm" data-id="element-790">Gut Health & Detoxification</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-791">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-792">Month 4:</p>
                      <p className="text-sm" data-id="element-793">
                        Brain Regeneration & Cognitive Enhancement
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-794">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-795">Month 5:</p>
                      <p className="text-sm" data-id="element-796">
                        Cardiovascular & Metabolic Optimization
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-797">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-798">Month 6:</p>
                      <p className="text-sm" data-id="element-799">Joint, Bone & Cartilage Repair</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-800">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-801">Month 7:</p>
                      <p className="text-sm" data-id="element-802">Energy, Performance & Endurance</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-803">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-804">Month 8:</p>
                      <p className="text-sm" data-id="element-805">Men's Hormonal Optimization</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-806">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-807">Month 9:</p>
                      <p className="text-sm" data-id="element-808">Women's Hormonal Balance</p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-809">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-810">
                        Month 10:
                      </p>
                      <p className="text-sm" data-id="element-811">
                        Injury Repair & Vision Restoration
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-812">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-813">
                        Month 11:
                      </p>
                      <p className="text-sm" data-id="element-814">
                        Immune Defense & Respiratory Health
                      </p>
                    </div>
                    <div className="bg-purple-950/30 p-4 rounded border border-purple-500/30" data-id="element-815">
                      <p className="font-bold text-purple-400 mb-1" data-id="element-816">
                        Month 12:
                      </p>
                      <p className="text-sm" data-id="element-817">
                        Anti-Aging, Longevity & Cancer Prevention
                      </p>
                    </div>
                  </div>

                  <p className="text-xl font-bold text-stone-100" data-id="element-818">
                    The idea is that by the end of 12 months, you won't just
                    have random knowledge.
                  </p>

                  <p className="text-2xl font-bold text-purple-400 text-center my-6" data-id="element-819">
                    You'll have systematic mastery across every major category
                    of biological optimization.
                  </p>

                  <div className="bg-amber-950/30 border-l-4 border-amber-500 p-6 mt-6" data-id="element-820">
                    <p className="text-amber-400 font-bold mb-2" data-id="element-821">
                      BUT PLEASE NOTE:
                    </p>
                    <p className="text-stone-100" data-id="element-822">
                      This is a loose roadmap. The exact theme for each month is
                      subject to change. The actual theme will be specified and
                      confirmed before the next month's sessions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* EXCLUSIVE BONUSES */}
            <div className="mb-16" data-id="element-823">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-4 sm:p-6 md:p-8 rounded-lg" data-id="element-824">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-825">
                  <Gift className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400 flex-shrink-0" data-id="element-826" />
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-400" data-id="element-827">
                    ✓ EXCLUSIVE BONUSES THROUGHOUT THE YEAR
                  </h4>
                </div>

                <div className="space-y-6 text-lg text-stone-300" data-id="element-828">
                  <p className="text-xl text-stone-100 font-semibold" data-id="element-829">
                    As a member of PSI PRO, you'll receive high-value bonuses
                    provided throughout the year.
                  </p>

                  <p className="text-stone-100 font-bold" data-id="element-830">Examples include:</p>

                  {/* PURITY REPORTS */}
                  <div className="bg-emerald-950/30 border-l-4 border-emerald-500 p-6 rounded-r-lg" data-id="element-831">
                    <h5 className="text-2xl font-bold text-emerald-400 mb-4" data-id="element-832">
                      PURITY REPORTS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-833">
                      Know Exactly What You're Actually Buying
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-834">
                      Whenever we do third-party vendor testing, we'll show you
                      exactly which products are legitimate and which are bunk.
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-835">
                      We send popular peptide products to the lab and share the
                      mass spec results with you.
                    </p>
                    <p className="text-emerald-400 font-bold" data-id="element-836">
                      This alone can save you thousands in wasted purchases.
                    </p>
                    <p className="text-stone-400 italic text-sm mt-2" data-id="element-837">
                      Because buying peptides that don't actually contain what
                      the label claims is worse than not taking anything at all.
                    </p>
                  </div>

                  {/* MEMBERSHIP-ONLY DISCOUNTS */}
                  <div className="bg-blue-950/30 border-l-4 border-blue-500 p-6 rounded-r-lg" data-id="element-838">
                    <h5 className="text-2xl font-bold text-blue-400 mb-4" data-id="element-839">
                      MEMBERSHIP-ONLY DISCOUNTS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-840">
                      Potentially Pay For Your Membership Through Savings Alone
                    </p>
                    <p className="text-stone-300 mb-2" data-id="element-841">
                      Get extra discounts that are ONLY available to active
                      members.
                    </p>
                    <div className="space-y-2 pl-4 my-3" data-id="element-842">
                      <p className="text-stone-300" data-id="element-843">
                        • Exclusive deals on high-quality peptide sources.
                      </p>
                      <p className="text-stone-300" data-id="element-844">
                        • Special pricing on bloodwork and testing.
                      </p>
                      <p className="text-stone-300" data-id="element-845">
                        • Access to vetted vendors at rates you can't get
                        anywhere else.
                      </p>
                    </div>
                    <p className="text-blue-400 font-bold" data-id="element-846">
                      This can literally pay for months of the membership
                      itself.
                    </p>
                  </div>

                  {/* GUEST ADVISORS */}
                  <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 rounded-r-lg" data-id="element-847">
                    <h5 className="text-2xl font-bold text-purple-400 mb-4" data-id="element-848">
                      GUEST ADVISORS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-849">
                      Expand Your Access Beyond Brenden & Ali
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-850">
                      We'll occasionally bring in other trusted doctors,
                      geneticists, or high-quality, vetted professionals who can
                      provide expert guidance in complementary areas.
                    </p>
                    <div className="space-y-2 pl-4" data-id="element-851">
                      <p className="text-stone-300" data-id="element-852">
                        Longevity physicians who work with billionaires.
                      </p>
                      <p className="text-stone-300" data-id="element-853">
                        Geneticists who understand epigenetic expression.
                      </p>
                      <p className="text-stone-300" data-id="element-854">
                        Elite performance coaches who optimize Olympic athletes.
                      </p>
                    </div>
                    <p className="text-purple-400 font-bold mt-3" data-id="element-855">
                      Expanding your intelligence network to a curated circle of
                      true experts.
                    </p>
                  </div>

                  {/* EXCLUSIVE CASE STUDY BREAKDOWNS */}
                  <div className="bg-cyan-950/30 border-l-4 border-cyan-500 p-6 rounded-r-lg" data-id="element-856">
                    <h5 className="text-2xl font-bold text-cyan-400 mb-4" data-id="element-857">
                      EXCLUSIVE CASE STUDY BREAKDOWNS
                    </h5>
                    <p className="text-xl font-semibold text-stone-100 mb-3" data-id="element-858">
                      Learn From Real "Impossible" Transformations
                    </p>
                    <p className="text-stone-300 mb-3" data-id="element-859">
                      See real client transformations with:
                    </p>
                    <div className="space-y-2 pl-4 mb-4" data-id="element-860">
                      <p className="flex items-start gap-3" data-id="element-861">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-862">→</span>
                        <span data-id="element-863">Before/after bloodwork</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-864">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-865">→</span>
                        <span data-id="element-866">Complete protocols used</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-867">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-868">→</span>
                        <span data-id="element-869">Timeline to results</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-870">
                        <span className="text-cyan-400 flex-shrink-0" data-id="element-871">→</span>
                        <span data-id="element-872">Obstacles encountered and overcome</span>
                      </p>
                    </div>

                    <p className="text-stone-100 font-bold mb-2" data-id="element-873">
                      Not hypothetical examples. Real transformations.
                    </p>

                    <div className="space-y-4 mt-4 text-sm" data-id="element-874">
                      <p className="text-stone-300" data-id="element-875">
                        <span className="font-bold text-cyan-400" data-id="element-876">Glen,</span>{' '}
                        the nephrologist who came to us with severe heart
                        failure (29% ejection fraction) and was being evaluated
                        for a transplant. We helped him restore his heart
                        function to normal (52%) and get back to work.
                      </p>
                      <p className="text-stone-300" data-id="element-877">
                        <span className="font-bold text-cyan-400" data-id="element-878">Emily,</span>{' '}
                        the lawyer whose cognitive decline was threatening her
                        career. We helped her increase her working memory
                        accuracy from 68% to 93% and cut her processing speed
                        time in half.
                      </p>
                      <p className="text-stone-300" data-id="element-879">
                        <span className="font-bold text-cyan-400" data-id="element-880">
                          Michael,
                        </span>{' '}
                        who could barely walk 160 meters due to osteoarthritis.
                        After our protocol, he improved his walking distance by
                        425% and tapered off his pain meds completely.
                      </p>
                      <p className="text-stone-300" data-id="element-881">
                        We've even had clients who were told their diabetes was
                        "genetic" and permanent. We helped them lose 120 pounds,
                        get off all medication, and drop their HbA1C from 8.2%
                        to a completely non-diabetic 5.3%.
                      </p>
                    </div>

                    <p className="text-cyan-400 font-bold mt-4" data-id="element-882">
                      Learn from actual case studies that prove what's possible.
                    </p>
                    <p className="text-stone-300 mt-2" data-id="element-883">
                      See exactly what was done, why it was done, and what
                      results it produced.
                    </p>
                    <p className="text-stone-300" data-id="element-884">
                      Then apply those insights to your own situation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FOUNDING MEMBER STATUS */}
            <div data-id="element-885">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-8 rounded-lg" data-id="element-886">
                <div className="flex items-center gap-4 mb-6" data-id="element-887">
                  <Crown className="w-10 h-10 text-amber-400" data-id="element-888" />
                  <h4 className="text-3xl font-bold text-amber-400" data-id="element-889">
                    ✓ PRO MEMBER STATUS
                  </h4>
                </div>

                <div className="space-y-4 text-lg text-stone-300" data-id="element-890">
                  <p className="text-xl text-stone-100 font-semibold" data-id="element-891">
                    As you progress through your 12-month journey, you'll gain
                    PRO Member status.
                  </p>

                  <p className="text-xl font-bold text-amber-400 mb-4" data-id="element-892">
                    This means:
                  </p>

                  <div className="space-y-4" data-id="element-893">
                    <p className="flex items-start gap-3" data-id="element-894">
                      <span className="text-amber-400 flex-shrink-0" data-id="element-895">→</span>
                      <span className="text-stone-100" data-id="element-896">
                        You're locked into your PRO member price{' '}
                        <span className="font-bold text-amber-400" data-id="element-897">
                          FOREVER
                        </span>{' '}
                        (as long as you stay subscribed)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-898">
                      <span className="text-amber-400 flex-shrink-0" data-id="element-899">→</span>
                      <span className="text-stone-100" data-id="element-900">
                        You get first priority to apply for higher tiers
                        (Precision Cell Ultra)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-901">
                      <span className="text-amber-400 flex-shrink-0" data-id="element-902">→</span>
                      <span className="text-stone-100" data-id="element-903">
                        You're part of the original inner circle — the elite
                        movers who saw this opportunity and took action
                      </span>
                    </p>
                  </div>

                  <div className="bg-red-950/30 border-l-4 border-red-500 p-6 mt-6" data-id="element-904">
                    <p className="text-xl font-bold text-red-400 mb-2" data-id="element-905">
                      After this deadline, this offer disappears.
                    </p>
                    <p className="text-stone-100 mb-2" data-id="element-906">
                      The price will NEVER be this low again.
                    </p>
                    <p className="text-stone-100" data-id="element-907">
                      And you'll never get another chance to lock in founding
                      member pricing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}